import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Edit, Trash2, Search } from 'lucide-react';

const RESOURCES = [
  'users', 'roles', 'permissions', 'license', 'departments',
  'customers', 'vendors', 'inventory', 'sales_orders', 'production',
  'packing_lists', 'labels', 'coa', 'indents', 'purchase_orders',
  'mrn', 'qc', 'debit_notes', 'tally', 'audit_logs'
];

const ACTIONS = ['view', 'add', 'edit', 'delete', 'approve', 'export'];

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', permissions: {} });

  useEffect(() => { fetchRoles(); }, []);

  const fetchRoles = async () => {
    try { const res = await api.get('/admin/roles'); setRoles(res.data); }
    catch (err) { console.error('Failed to fetch roles:', err); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingRole(null);
    setFormData({ name: '', description: '', permissions: {} });
    setShowModal(true);
  };

  const openEdit = (role) => {
    setEditingRole(role);
    setFormData({ name: role.name, description: role.description || '', permissions: { ...role.permissions } });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRole) {
        await api.put(`/admin/roles/${editingRole.id}`, formData);
      } else {
        await api.post('/admin/roles', formData);
      }
      setShowModal(false);
      fetchRoles();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save role'); }
  };

  const deleteRole = async (id, name) => {
    if (!window.confirm(`Delete role "${name}"? Users assigned to this role will lose access.`)) return;
    try { await api.delete(`/admin/roles/${id}`); fetchRoles(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed to delete role'); }
  };

  const togglePermission = (resource, action) => {
    const perms = { ...formData.permissions };
    if (!perms[resource]) perms[resource] = [];
    const idx = perms[resource].indexOf(action);
    if (idx >= 0) {
      perms[resource] = perms[resource].filter(a => a !== action);
      if (perms[resource].length === 0) delete perms[resource];
    } else {
      perms[resource] = [...perms[resource], action];
    }
    setFormData({ ...formData, permissions: perms });
  };

  const selectAllForResource = (resource) => {
    const perms = { ...formData.permissions };
    const current = perms[resource] || [];
    if (current.length === ACTIONS.length) {
      delete perms[resource];
    } else {
      perms[resource] = [...ACTIONS];
    }
    setFormData({ ...formData, permissions: perms });
  };

  const hasPermission = (resource, action) => {
    return (formData.permissions[resource] || []).includes(action);
  };

  return (
    <div data-testid="roles-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Roles & Permissions</h1>
        <button onClick={openCreate} className="btn btn-primary" data-testid="add-role-btn"><Plus size={16} /> Add Role</button>
      </div>

      <div className="card">
        {loading ? <div data-testid="loading-indicator">Loading roles...</div> : (
          <div>
            {roles.map(role => (
              <div key={role.id} style={{ padding: '16px', borderBottom: '1px solid var(--border-default)' }} data-testid={`role-card-${role.name.toLowerCase()}`}>
                <div className="flex items-center justify-between" style={{ marginBottom: '8px' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>{role.name}</h3>
                    {role.description && <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>{role.description}</p>}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(role)} className="btn btn-ghost btn-sm" title="Edit" data-testid={`edit-role-${role.name.toLowerCase()}`}><Edit size={14} /></button>
                    <button onClick={() => deleteRole(role.id, role.name)} className="btn btn-ghost btn-sm" title="Delete" data-testid={`delete-role-${role.name.toLowerCase()}`}><Trash2 size={14} color="#DC2626" /></button>
                  </div>
                </div>
                <div className="grid grid-cols-4" style={{ gap: '4px' }}>
                  {Object.entries(role.permissions).map(([resource, actions]) => (
                    <div key={resource} style={{ marginBottom: '4px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, textTransform: 'capitalize' }}>{resource.replace(/_/g, ' ')}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{actions.join(', ')}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create / Edit Role Modal */}
      {showModal && (
        <div className="modal-overlay" data-testid="role-modal">
          <div className="modal-content" style={{ maxWidth: '900px', maxHeight: '85vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editingRole ? 'Edit Role' : 'Create Role'}</h2>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Role Name *</label>
                    <input type="text" className="form-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="role-name-input" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <input type="text" className="form-input" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} data-testid="role-desc-input" />
                  </div>
                </div>

                <h3 style={{ fontSize: '14px', fontWeight: 600, marginTop: '16px', marginBottom: '12px' }}>Permission Matrix</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', fontSize: '12px' }}>
                    <thead>
                      <tr>
                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>Resource</th>
                        {ACTIONS.map(a => <th key={a} style={{ padding: '6px 8px', textAlign: 'center', textTransform: 'capitalize' }}>{a}</th>)}
                        <th style={{ padding: '6px 8px', textAlign: 'center' }}>All</th>
                      </tr>
                    </thead>
                    <tbody>
                      {RESOURCES.map(resource => (
                        <tr key={resource} style={{ borderBottom: '1px solid var(--border-default)' }}>
                          <td style={{ padding: '6px 8px', fontWeight: 500, textTransform: 'capitalize' }}>{resource.replace(/_/g, ' ')}</td>
                          {ACTIONS.map(action => (
                            <td key={action} style={{ padding: '6px 8px', textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={hasPermission(resource, action)}
                                onChange={() => togglePermission(resource, action)}
                                data-testid={`perm-${resource}-${action}`}
                              />
                            </td>
                          ))}
                          <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={(formData.permissions[resource] || []).length === ACTIONS.length}
                              onChange={() => selectAllForResource(resource)}
                              data-testid={`perm-${resource}-all`}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-role-btn">{editingRole ? 'Update Role' : 'Create Role'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Roles;
